// UserForm component is used for adding, editing, and managing user information.

import { useContext, useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import {
  TDropdownOptions,
  TResponseModel,
  TRole,
  TUser,
} from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  AddEditUser,
  GetConfigParamDataList,
  GetCustomerList,
  GetRoleList,
  GetServicePartnerList,
  GetSpecificUserMasterData,
  GetStateList,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { CONFIG_GROUPS, STAKE_HOLDERS } from "../../../utils/constants";
import CustomDropdown from "../../../components/custom-dropdown/custom-dropdown";
import { UserContext } from "../../../context";
import EncryptUtils from "../../../utils/encrypt";
import styles from "./users.module.css";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { CloseOutlined, CheckOutlined } from "@ant-design/icons";

type DrawerProps = {
  id: number;
  isOpen?: any;
  activeStake: string;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * UserForm component is used for adding, editing, and managing user information.
 * It provides a form with fields for user details such as display name, email, phone number,
 * stakeholder, role, and organization. The component handles fetching and setting dropdown options
 * for stakeholder types, roles, and organizations based on the selected values. It also manages form
 * submission to add or update user records. The component also fetches specific user data if an ID is provided.
 *
 * Props:
 * - id: number - The ID of the user to edit; 0 for adding a new user.
 * - activeStake: string - The type of stakeholder (e.g., 'CUSTOMER', 'SERVICEPARTNER').
 * - onCloseDrawer: function - Callback function to close the form drawer.
 * - onRefreshList: function - Callback function to refresh the user list after adding or editing a user.
 */
export default function UserForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [stakeHolderOptions, setStakeHolderOptions] = useState<
    TDropdownOptions[]
  >([]);
  const context: any = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user);
  const [endPoint, setEndPoint] = useState<string>(() => {
    if (activeUser?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      return GetCustomerList;
    } else if (activeUser?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER) {
      return "GetStakeHolderList";
    } else {
      return ""; // Default value if no matching condition
    }
  });
  const [roleOptions, setRoleOptions] = useState<TRole[]>([]);
  const [selectedRole, setSelectedRole] = useState<TRole>();
  const [role, setRole] = useState<TRole[]>([]);
  const [stateOptions, setStateOptions] = useState<TDropdownOptions[]>([]);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  // const [locationTypes, setLocationTypes] = useState<TDropdownOptions[]>([]);

  // Hook to handle on load operations
  useEffect(() => {
    /**
     * Fetches data for the form, such as the stakeholder types, customer/service partner
     * list, and role options. If the active stake is 'CUSTOMER' or 'SERVICEPARTNER', it
     * fetches the corresponding list and sets the endpoint. Otherwise, it sets the orgId
     * field to 1 and sets the endpoint to an empty string. It then fetches the role options
     * based on the active stakeholder ID.
     */

    const fetchData = async () => {
      fetchSpecificUser();

      if (props.activeStake === STAKE_HOLDERS.CUSTOMER) {
        form.setFieldValue("orgId", null);
        setEndPoint(GetCustomerList);
      } else if (props.activeStake === STAKE_HOLDERS.SERVICEPARTNER) {
        form.setFieldValue("orgId", null);
        setEndPoint(GetServicePartnerList);
        if (activeUser?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER) {
          fetchCustomerList();
        }
      } else {
        form.setFieldValue("orgId", 1);
        setEndPoint("");
      }

      const stakeHolderTypes = await fetchConfigParams(
        CONFIG_GROUPS.STAKEHOLDERS
      );
      setStakeHolderOptions(stakeHolderTypes);
      let activeId: number | undefined;

      const activeStakeHolder = stakeHolderTypes.find(
        (item: TDropdownOptions) =>
          item?.data?.paramUniqueId === props.activeStake
      );

      if (activeStakeHolder) {
        activeId = activeStakeHolder.value;
        form.setFieldValue("stakeHolderId", activeId);
      }

      fetchRoleOptions(activeId || 0);
    };
    fetchData();
  }, [props.activeStake]);

  const fetchStates = async () => {
    try {
      const payload = {
        data: {
          countryId: 1,
        },
      };

      const response: TResponseModel = await post(GetStateList, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setStateOptions(Utils.mapDropdownOptions(data));
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const fetchCustomerList = async (servicePartnerId: number = 0) => {
    try {
      let requestPayload;
      if (servicePartnerId) {
        requestPayload = {
          data: {
            pageSize: -1,
            currentPage: 1,
            servicePartnerId: servicePartnerId,
            isEngineerExclusiveView: 1,
          },
        };
      } else {
        requestPayload = {
          data: {
            pageSize: -1,
            currentPage: 1,
          },
        };
      }

      const response: TResponseModel = await post(
        GetCustomerList,
        requestPayload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setCustomerOptions(Utils.mapDropdownOptions(data));
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const fetchData = async () => {
    // to fetch the customerID filter if available
    // const newUrl = new URL(window.location.href);
    // const queryParams = newUrl.searchParams;

    // Access the customerId and customerName query parameters
    // setDealerId(queryParams.get("id") || ("0" as any));

    setIsLoading(true);
    // await fetchCountry(); // Fetch countries
    await fetchStates(); // Fetch states
    // const locations = await fetchConfigParams(CONFIG_GROUPS.LOCATIONTYPE);
    // setLocationTypes(locations);
    // const sla = await fetchConfigParams(CONFIG_GROUPS.SLAOPTIONS);
    // setSLAOptions(sla);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchStates();
    fetchData();
  }, []);

  /**
   * Fetches the list of roles for the given stake holder ID.
   * If the response is successful, it sets the role options state with the mapped dropdown options.
   * If the response is not successful, it shows an error toast.
   * Finally, it sets isLoading to false.
   * @param stakeHolderId The stake holder ID
   */
  const fetchRoleOptions = async (stakeHolderId: number) => {
    try {
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
          stakeHolderId,
        },
      };

      const response: TResponseModel = await post(GetRoleList, requestPayload);
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setRole(data);
        setRoleOptions(Utils.mapDropdownOptions(data));
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  const defaultValues: TUser = {
    id: 0,
    displayName: "",
    emailId: "",
    mobileNo: "",
    roleId: null,
    userName: "",
    stakeHolderUniqueId: props.activeStake,
    stakeHolderId:
      activeUser?.stakeHolderName === STAKE_HOLDERS.IDEX
        ? null
        : activeUser?.stakeHolderId || null,
    stakeHolderName: "",
    orgId: null,
    orgName: "",
    location: "",
    city: "",
    state: "",
    customerName: "",
    isExclusive: 0,
  };

  // To get the list of parameter options
  const fetchConfigParams = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          groupUniqueId,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          return Utils.mapDropdownOptions(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  // Handle on submit event
  const onFinish = async (values: TUser) => {
    try {
      const { roleId, orgId, password, ...restValues } = values;
      const roles = [];
      roles.push({ id: roleId, name: "" });
      const payload = {
        data: {
          id: props.id ? props.id : 0,
          orgId: props.activeStake === STAKE_HOLDERS.IDEX ? 1 : orgId,
          password: props?.id
            ? EncryptUtils.encrypt(password || "")
            : EncryptUtils.encrypt(Utils.generatePassword()),
          ...restValues,
          roles,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditUser, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const fetchSpecificUser = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificUserMasterData, payload);
        const { data } = response;
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const { roles, password, customerId, ...restValues } = response
            .data[0] as TUser;
          setSelectedRole(roles?.[0]);
          if (data[0]?.isExclusive) {
            await fetchCustomerList(data[0]?.orgId);
          }
          form.setFieldsValue(restValues);
          form.setFieldValue("roleId", roles?.[0]?.id);
          form.setFieldValue("password", EncryptUtils.decrypt(password || ""));
          customerId && form.setFieldValue("customerId", customerId);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a User`}
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="displayName"
                label="Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter display name"),
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  placeholder="Enter the display name"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="emailId"
                label="E-mail"
                rules={[
                  {
                    type: "email",
                    message: "The input is not valid E-mail!",
                  },
                  {
                    required: true,
                    message: "Please enter your E-mail!",
                  },
                ]}
              >
                <Input
                  disabled={props.id ? true : false}
                  autoComplete="off"
                  placeholder="Enter the email address"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="mobileNo"
                label="Phone Number"
                rules={[
                  {
                    required: true,
                    message: "Please enter your phone number!",
                  },
                  {
                    pattern: /^\d{10}$/,
                    message: "Phone number must be exactly 10 digits!",
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  maxLength={10}
                  placeholder="Enter the phone number"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="stakeHolderId"
                label="Stake Holder"
                rules={[
                  { required: true, message: "Please select stake holder" },
                ]}
              >
                <Select
                  disabled
                  placeholder="Select stake holder"
                  options={stakeHolderOptions}
                  onChange={(value, option) => {
                    const stakeUniqueId = (option as TDropdownOptions).data
                      .paramUniqueId;

                    form.setFieldValue("roleId", null);

                    form.setFieldValue("stakeHolderId", value);
                    form.setFieldValue("stakeHolderUniqueId", stakeUniqueId);
                  }}
                ></Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="roleId"
                label="Role"
                rules={[{ required: true, message: "Please select role" }]}
              >
                <Select
                  disabled={false}
                  placeholder="Select role"
                  options={roleOptions}
                  onChange={(value, option) => {
                    form.setFieldValue("roleId", value);
                    setSelectedRole((option as any)?.data as TRole);
                  }}
                ></Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              {props.activeStake === STAKE_HOLDERS.IDEX ? null : (
                <Form.Item
                  name="orgId"
                  label={
                    props.activeStake === STAKE_HOLDERS.CUSTOMER
                      ? "Customer"
                      : props.activeStake === STAKE_HOLDERS.SERVICEPARTNER
                        ? "Service Partner"
                        : " "
                  }
                  dependencies={["stakeHolderUniqueId"]}
                  rules={[
                    {
                      required:
                        props.activeStake === STAKE_HOLDERS.IDEX ? false : true,
                      message: "Please select customer/partner",
                    },
                  ]}
                >
                  <CustomDropdown
                    isDisabled={props?.id ? true : false}
                    endPoint={endPoint}
                    value={form.getFieldValue("orgId")}
                    selectedName={form.getFieldValue("orgName")}
                    onChange={(selected: any) => {
                      form.setFieldValue("orgId", selected.value);
                      form.setFieldValue("orgName", selected.label);

                      if (props.activeStake === STAKE_HOLDERS.SERVICEPARTNER) {
                        fetchCustomerList(selected?.value);
                      }
                    }}
                    label=""
                    placeHolder="Select customer/partner"
                  ></CustomDropdown>
                </Form.Item>
              )}
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select
                  disabled={false}
                  placeholder="Select status"
                  options={[
                    { value: 1, label: "Active" },
                    { value: 0, label: "Inactive" },
                  ]}
                ></Select>
              </Form.Item>
            </Col>

            {props?.id ? (
              <Col span={12}>
                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your password!",
                    },
                    {
                      pattern:
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                      message:
                        "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
                    },
                  ]}
                >
                  <Input.Password
                    autoComplete="off"
                    placeholder="Enter the password"
                  />
                </Form.Item>
              </Col>
            ) : (
              ""
            )}

            {selectedRole?.roleUniqueId === "SERVICEENGINEER" ? (
              <>
                <Col span={12}>
                  <Form.Item
                    name="location"
                    label={<span className={styles.customLabel}>Location</span>}
                    rules={[
                      {
                        required: true,
                        validator: requiredFieldValidator(
                          "enter your location"
                        ),
                      },
                    ]}
                  >
                    <Input
                      autoComplete="off"
                      placeholder="Enter location type"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="stateId"
                    label={<span className={styles.customLabel}>State</span>}
                    rules={[{ required: true, message: "Please choose state" }]}
                  >
                    <Select
                      showSearch
                      placeholder="Select state"
                      options={stateOptions}
                      // onChange={handleStateChange}
                      filterOption={(input, option) =>
                        option?.label
                          ? option.label
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          : false
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="districtName"
                    label={<span className={styles.customLabel}>City</span>}
                    rules={[
                      {
                        required: true,
                        validator: requiredFieldValidator("enter city"),
                      },
                    ]}
                  >
                    <Input autoComplete="off" placeholder="Enter city" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="isExclusive"
                    label={
                      <span className={styles.customLabel}>Is Exclusive</span>
                    }
                    rules={[
                      {
                        required: true,
                        message: "Please choose exclusive status",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      defaultValue={0}
                      placeholder="Select status"
                      options={[
                        {
                          value: 1,
                          label: "Yes",
                        },
                        {
                          value: 0,
                          label: "No",
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, current) =>
                    prev.isExclusive !== current.isExclusive
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue("isExclusive") === 1 ? (
                      <Col span={12}>
                        <Form.Item
                          name="customerId"
                          label={
                            <span className={styles.customLabel}>Customer</span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please choose a customer",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Select customer"
                            options={customerOptions}
                          />
                        </Form.Item>
                      </Col>
                    ) : null
                  }
                </Form.Item>
              </>
            ) : null}
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              className="btnDimensions"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
            >
              {props.id ? "Update" : "Add"}
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={props.onCloseDrawer}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
